import { FunctionComponent } from "react"
import { useNavigate } from "react-router-dom"
import { useGetOrganizationResultsAccessStatusQuery } from "../../app/services/meseeknaApiV2"
import { RootState, useTypedSelector } from "../../app/store"
import { useOrganizationRecovery } from "../../hooks/useOrganizationRecovery"
import RecruiterMicrolearningDashboard from "./RecruiterMicrolearningDashboard"

const MicrolearningSection: FunctionComponent = () => {
  const navigate = useNavigate()

  const authStore = useTypedSelector((state: RootState) => state.authSlice)
  const { isRecovering } = useOrganizationRecovery()

  // Get effective organization ID
  const effectiveOrgId = Number(
    authStore.organization?.id || authStore.user?.organizationId || 0,
  )

  // Fetch unlock status from API
  const { data: accessStatusData, isLoading } = useGetOrganizationResultsAccessStatusQuery(
    effectiveOrgId,
    { skip: !effectiveOrgId || isRecovering }
  )

  // Unlock based on API response - hasAccess is true when resultsApprovalStatus is 'approved'
  const isUnlocked = accessStatusData?.data?.hasAccess ?? false

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-full bg-gray-50 py-12 px-6 flex items-center justify-center">
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-4 h-4 bg-teal-400 rounded-full"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // When unlocked, show the full recruiter microlearning dashboard
  if (isUnlocked) {
    return <RecruiterMicrolearningDashboard />
  }

  // Show locked state with marketing copy
  return (
    <div className="min-h-full bg-gray-50 py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Precision Development</h1>
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded mt-1">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Unlocks after simulation completion
              </span>
            </div>
          </div>

          <p className="text-gray-600 leading-relaxed mb-4">
            Generic training fails 90% of learners. Development that works starts with knowing exactly what to develop.
          </p>
          <p className="text-gray-600 leading-relaxed mb-6">
            Once your team completes their simulations, each person unlocks a 52-week learning track built around their actual cognitive profile—modules that target the exact competencies where they&apos;ll see real growth.
          </p>

          <div className="flex flex-col items-start">
            <button
              onClick={() => navigate("/dashboard/recruiter-admin/users/all")}
              className="px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors mb-2"
            >
              Encourage Your Team to Complete
            </button>
            <span className="text-gray-500 text-xs">The simulation takes 30–45 minutes. The development insights last.</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MicrolearningSection
